function normalizeGridFilterText(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function getComparableValues(cellValue, filterValue, isPhone) {
  let cellText = normalizeGridFilterText(cellValue);
  let filterText = normalizeGridFilterText(filterValue);

  // Phone searches can span formatting separators. Keep letters meaningful so
  // text such as extensions or invalid sheet values can still be searched.
  if (isPhone && /^\+?[\d\s().-]+$/.test(filterText) && /\d/.test(filterText)) {
    // An explicit '+' is meaningful when searching for a calling code.
    const separators = filterText.startsWith('+') ? /[\s().-]/g : /[+\s().-]/g;
    cellText = cellText.replace(separators, '');
    filterText = filterText.replace(separators, '');
  }

  return [cellText, filterText];
}

export function doesGridCellMatchFilter(cellValue, filterItem, { isPhone = false } = {}) {
  const rawFilterValue = filterItem?.value;
  const [cellText, filterText] = getComparableValues(cellValue, rawFilterValue, isPhone);

  switch (filterItem?.operator) {
    case 'contains':
      return cellText.includes(filterText);
    case 'doesNotContain':
      return !cellText.includes(filterText);
    case 'equals':
      return cellText === filterText;
    case 'doesNotEqual':
      return cellText !== filterText;
    case 'startsWith':
      return cellText.startsWith(filterText);
    case 'endsWith':
      return cellText.endsWith(filterText);
    case 'isEmpty':
      return normalizeGridFilterText(cellValue) === '';
    case 'isNotEmpty':
      return normalizeGridFilterText(cellValue) !== '';
    case 'isAnyOf': {
      const filterValues = Array.isArray(rawFilterValue) ? rawFilterValue : [rawFilterValue];

      return filterValues.some((value) => {
        const [comparableCell, comparableFilter] = getComparableValues(cellValue, value, isPhone);
        return comparableCell === comparableFilter;
      });
    }
    default:
      return true;
  }
}

export function filterPhoneValueOptions(options, inputValue, limit = 100) {
  return options.filter((option) => doesGridCellMatchFilter(
    option,
    { operator: 'contains', value: inputValue },
    { isPhone: true }
  )).slice(0, limit);
}

export function createGridSearchIndex(values, phoneValues = []) {
  return {
    text: values.map(normalizeGridFilterText).join('\n'),
    phones: phoneValues.map((value) => normalizeGridFilterText(value).replace(/[\s().-]/g, '')),
  };
}

export function getGridSearchTerms(query) {
  const text = normalizeGridFilterText(query);
  if (!text) return [];
  // Treat a pasted, formatted phone number as one contiguous digit sequence.
  const terms = /^\+?[\d\s().-]+$/.test(text) && /\d/.test(text) ? [text] : text.split(/\s+/);
  return terms.map((term) => ({
    text: term,
    phone: /^\+?[\d\s().-]+$/.test(term) && /\d/.test(term) ? term.replace(/[\s().-]/g, '') : '',
  }));
}

export function doesGridSearchMatch(index, terms) {
  return terms.every((term) => index.text.includes(term.text)
    || (term.phone && index.phones.some((phone) => phone.includes(term.phone))));
}
